const d3 = require('d3')
const moment = require('moment');
const { parseDate } = require('../utils/formatters.js');
const { wrap } = require( '../utils/shared-functions.js');
const { temperatureConvert } = require('../utils/converter.js');

const HIGH_TEMPERATURE_LEVEL_COLOR = '#ED6A4F';
const NORMAL_TEMPERATURE_LEVEL_COLOR = '#9AC03A';
const LOW_TEMPERATURE_LEVEL_COLOR = '#66B2D6';
const NORMAL_TEMPERATURE_LEVEL_BG_COLOR = '#EDF3DD';
const EMPTY_COLOR = '#000000';
const DEVICE_COLOR = '#6DA758';
const MANUAL_COLOR = '#558AC2';
const EXTERNAL_COLOR = '#CF42F0';

const margin = { top: 40, right: 30, bottom: 40, left: 50 };

exports.createTempChart = function (rawData, manualData, externalData, thresholds,onClick,width,height,temperature_unit,thresholdsStatus) {

  const { 'Body temperature': temperature_threshold } = thresholds;
  temperature_threshold.low = temperatureConvert(temperature_threshold.low, temperature_unit);
  temperature_threshold.high = temperatureConvert(temperature_threshold.high, temperature_unit);
  const TICK_VALUES = [35, 36, 37, 38, 39, 40, 41, 42].map((val) => temperatureConvert(val, temperature_unit));
  const MAX_TEMPERATURE = {
    '°C': 42,
    '°F': temperatureConvert(42, temperature_unit),
  };
  const MIN_TEMPERATURE = {
    '°C': 35,
    '°F': temperatureConvert(35, temperature_unit),
  };
  const emptyMap = {};

  let data = [];
  const data_device = rawData.map((d) => {
    const momentFormatted = moment(d['date']).format('YYYY-MM-DD');
    const date = parseDate(momentFormatted);
    emptyMap[momentFormatted] = d['value'] ? d['value'] : emptyMap[momentFormatted];
    const obj = {
      date,
      data: temperatureConvert(d['value'], temperature_unit),
      color: DEVICE_COLOR
    };
    return obj;
  });
  const data_manual = manualData.map((d) => {
    const momentFormatted = moment(d['date']).format('YYYY-MM-DD');
    const date = parseDate(momentFormatted);
    emptyMap[momentFormatted] = d['value'] ? d['value'] : emptyMap[momentFormatted];
    const obj = {
      date,
      data: temperatureConvert(d['value'], temperature_unit),
      color: MANUAL_COLOR,
    };
    return obj;
  });
  const data_external = externalData.map((d) => {
    const momentFormatted = moment(d['date']).format('YYYY-MM-DD');
    const date = parseDate(momentFormatted);
    emptyMap[momentFormatted] = d['value'] ? d['value'] : emptyMap[momentFormatted];
    const obj = {
      date,
      data: temperatureConvert(d['value'], temperature_unit),
      color: EXTERNAL_COLOR,
    };
    return obj;
  });
  data = data.concat(data_device).concat(data_manual).concat(data_external);

  const x = d3
    .scaleBand()
    .domain(d3.utcDay.range(data[0].date, +data[data.length - 1].date + 1))
    .range([margin.left, width - margin.right])
    .padding(0.4);

  const y = d3
    .scaleLinear()
    .domain([MIN_TEMPERATURE[temperature_unit], MAX_TEMPERATURE[temperature_unit]])
    .nice()
    .range([height - margin.bottom, margin.top]);

  const xAxis = (g) =>
    g.attr('transform', `translate(0,${height - margin.bottom - 2})`)
    .call(d3
        .axisBottom(x)
        .tickValues(d3.utcDay.every(1).range(data[0].date, +data[data.length - 1].date + 1))
        .tickFormat((d) => moment(d).format('ddd DD'))
        .tickSize(0)
    )
    .call((g) =>
    g.selectAll('.tick line')
      .clone()
      .attr('stroke-opacity', 0.2)
      .attr('transform', `translate(0,${-height + margin.top + margin.bottom + 3})`)
      .attr('y2', height - margin.top - margin.bottom)
  )
    .call((g) => g.select('.domain').remove());

  const yAxis = (g) =>
    g.attr('transform', `translate(${margin.left - 10},0)`)
    .call(d3.axisLeft(y).tickValues(TICK_VALUES).tickSize(0).tickPadding(10))
    .call((g) => g.selectAll('.tick line')
      .clone()
      .attr('stroke-opacity', 0.2)
      .attr('x2', width - margin.left - margin.right)
      )
    .call((g) => g.select('.domain').remove());

  const svg = d3.create('svg').attr('width', width).attr('height', height);

  if(thresholdsStatus === 'on'){
    const normalTemperatureBackgroundColor = svg
        .append('rect')
        .attr('x', x(data[0].date))
        .attr('y', y(temperature_threshold.high || temperatureConvert(37, temperature_unit)))
        .attr('width', Math.abs(x(data[data.length - 1].date) - x(data[0].date)) + x.bandwidth())
        .attr('height',
          Math.abs(
            y(temperature_threshold.high || TICK_VALUES[3] - 0.5) - y(temperature_threshold.low || TICK_VALUES[2] + 0.5)
          )
        )
        .attr('fill', NORMAL_TEMPERATURE_LEVEL_BG_COLOR)
        .attr('opacity', 1);
  }
  

  svg.append('g').call(xAxis).selectAll('.tick text').call(wrap, x.bandwidth());
  svg.append('g').call(yAxis);

  const xAxisLabelDate = svg
    .append('text')
    .attr('x', 20)
    .attr('y', height - margin.bottom + 20)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text('Date');

  const xAxisLabelDay = svg
    .append('text')
    .attr('x', 20)
    .attr('y', height - margin.bottom + 33)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text('Day');

  const yAxisLabelDate = svg
    .append('text')
    .attr('x', margin.left - 40)
    .attr('y', margin.top - 20)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(temperature_unit);

  const fixLabelXPosition = width / rawData.length / 3.7
  const g = svg
    .append('g')
    .selectAll('g')
    .data(data)
    .join('g')
    .attr('transform', (d) => `translate(${x(d.date) + fixLabelXPosition},0)`);

  const emptyGroup = g.append('g').attr('visibility', (d) => {
    const momentFormatted = moment(d['date']).format('YYYY-MM-DD');
    // using color condition in order to not render double empty bars
    return !emptyMap[momentFormatted] && d.color === MANUAL_COLOR ? 'visible' : 'hidden';
  });
  const emptyBar = emptyGroup
    .append('line')
    .attr('y1', (d) => y(TICK_VALUES[0]))
    .attr('y2', (d) => y(TICK_VALUES[TICK_VALUES.length - 1]))
    .attr('stroke-width', 36)
    .attr('stroke', EMPTY_COLOR)
    .attr('opacity', 0.07);

  const temperaturePointsGroup = g.append('g').attr('visibility', (d) =>  d.data ? 'visible' : 'hidden');

  const temperaturePoint = temperaturePointsGroup
    .append('circle')
    .attr('r', 3)
    .attr('cy', (d) => y(d.data))
    .attr('stroke-width', 1)
    .attr('fill', (d) => d.color)
    .attr('stroke', (d) => d.color)
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

  const gLegend = svg.append('g').attr('transform', (d) => `translate(${width-900},${margin.top - 15})`);

  const lowTemperatureLegendAverageCircle = gLegend
    .append('circle')
    .attr('cx', 485)
    .attr('cy', 4)
    .attr('r', 6)
    .attr('stroke-width', 1)
    .attr('fill', DEVICE_COLOR)
    .attr('stroke', DEVICE_COLOR);

  const lowTemperatureLegendAverageText = gLegend
    .append('text')
    .attr('x', 495)
    .attr('y', 9)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text('Device measurements');

  const normalTemperatureLegendAverageCircle = gLegend
    .append('circle')
    .attr('cx', 290)
    .attr('cy', 4)
    .attr('r', 6)
    .attr('stroke-width', 1)
    .attr('fill', MANUAL_COLOR)
    .attr('stroke', MANUAL_COLOR);

  const externalTemperatureLegendAverageText = gLegend
    .append('text')
    .attr('x', 300)
    .attr('y', 9)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text('Manual measurements'); // \u00A0 == space

  const externalTemperatureLegendAverageCircle = gLegend
    .append('circle')
    .attr('cx', 680)
    .attr('cy', 4)
    .attr('r', 6)
    .attr('stroke-width', 1)
    .attr('fill', EXTERNAL_COLOR)
    .attr('stroke', EXTERNAL_COLOR);

  const normalTemperatureLegendAverageText = gLegend
    .append('text')
    .attr('x', 690)
    .attr('y', 9)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text('External measurements'); // \u00A0 == space

  const missingValuesLegendRect = gLegend
    .append('rect')
    .attr('x', 120)
    .attr('y', 0)
    .attr('width', 10)
    .attr('height', 10)
    .attr('opacity', 0.11)
    .attr('fill', '#000000');

  const missingValuesLegendText = gLegend
    .append('text')
    .attr('x', 135)
    .attr('y', 9)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text('No measurements');

  return svg.node();
};

